import {
    DownloadableFile,
    downloadableFilesIterator,
} from './get-downloadable-file';
import axios, {
    type RawAxiosResponseHeaders,
    type AxiosProgressEvent,
    type AxiosResponseHeaders,
} from 'axios';
import { Observable, tap, map, from, mergeMap, share } from 'rxjs';
import { FileDownloadFailedError } from './errors';
import { downloadBlob } from '@precon-tools-suite-by-shared/browser-utils';
import type { TypedGraphQLQueryClient } from '@precon-tools-suite-by-shared/shared-graphql-client-js-generic';

export function redirectDownload(file: DownloadableFile) {
    window.location.href = file.url;
}

export type DownloadMessage_Requesting = {
    message: 'download:requesting';
    file: DownloadableFile;
};

export type DownloadMessage_Downloading = {
    message: 'download:downloading';
    file: DownloadableFile;
    progress: AxiosProgressEvent;
};

export type DownloadMessage_Success = {
    message: 'download:success';
    file: DownloadableFile;
    blob: Blob;
    status: number;
    statusText: string;
    headers: RawAxiosResponseHeaders | AxiosResponseHeaders;
};
export type DownloadMessage =
    | DownloadMessage_Requesting
    | DownloadMessage_Downloading
    | DownloadMessage_Success;

export function createObservableDownload(
    file: DownloadableFile
): Observable<DownloadMessage> {
    return new Observable<DownloadMessage>((subscriber) => {
        const abort = new AbortController();

        axios
            .request({
                url: file.url,
                method: 'get',
                responseType: 'blob',
                signal: abort.signal,
                onDownloadProgress: (progress) => {
                    subscriber.next({
                        message: 'download:downloading',
                        file,
                        progress,
                    });
                },
            })
            .then((response) => {
                subscriber.next({
                    message: 'download:success',
                    file,
                    blob: response.data,
                    status: response.status,
                    statusText: response.statusText,
                    headers: response.headers,
                });

                subscriber.complete();
            })
            .catch((error) => {
                subscriber.error(FileDownloadFailedError.fromAxiosError(error));
            });

        subscriber.next({
            message: 'download:requesting',
            file,
        });

        return () => {
            abort.abort();
        };
    });
}

export function downloadOnSuccessMessage() {
    return tap<DownloadMessage>((message) => {
        if (message.message == 'download:success') {
            downloadBlob(message.blob, message.file.filename);
        }
    });
}

export type SmallDownloadMessage = Omit<DownloadMessage, 'blob'>;

/**
 * Ensure that the blobs can be garbage-collected.
 */
export function omitBlobs() {
    return map<DownloadMessage, SmallDownloadMessage>((message) =>
        message.message === 'download:success'
            ? {
                  message: 'download:success',
                  file: message.file,
                  status: message.status,
                  statusText: message.statusText,
                  headers: message.headers,
              }
            : message
    );
}

export function downloadFiles(client: TypedGraphQLQueryClient, id: string) {
    return from(downloadableFilesIterator(client, id)).pipe(
        mergeMap((file) => createObservableDownload(file)),
        downloadOnSuccessMessage(),
        omitBlobs(),
        share()
    );
}
